import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import AdminDashboard from './admin/AdminDashboard';
import AdminUsers from './admin/AdminUsers';
import AdminMessages from './admin/AdminMessages';
import AdminRandomChat from './admin/AdminRandomChat';
import AdminPosts from './admin/AdminPosts';
import AdminFiles from './admin/AdminFiles';
import AdminReports from './admin/AdminReports';
import AdminFeedback from './admin/AdminFeedback';

const NAV = [
  { path: '', label: 'Dashboard', icon: '📊' },
  { path: 'users', label: 'Users', icon: '👥' },
  { path: 'messages', label: 'Messages', icon: '💬' },
  { path: 'random-chat', label: 'Random Chat', icon: '🎲' },
  { path: 'posts', label: 'Posts & Comments', icon: '📸' },
  { path: 'files', label: 'Files', icon: '📁' },
  { path: 'reports', label: 'Reports', icon: '🚨' },
  { path: 'feedback', label: 'Feedback', icon: '📣' },
];

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const isStaff = localStorage.getItem('is_staff') === 'true';
    const isSuperuser = localStorage.getItem('is_superuser') === 'true';
    const username = localStorage.getItem('username');

    // Try to verify with API
    api.get('/admin/stats/')
      .then(res => {
        setAdmin({ username, isStaff, isSuperuser, stats: res.data });
        setLoading(false);
      })
      .catch(err => {
        if (err.response?.status === 403 || err.response?.status === 401) {
          navigate('/chat', { replace: true });
        }
        setLoading(false);
      });
  }, [navigate]);

  const getActive = () => {
    const parts = location.pathname.split('/admin-panel/');
    return parts[1] || '';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#07070d' }}>
        <div style={{ width: 40, height: 40, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={S.root}>
      {/* Sidebar */}
      <aside style={{ ...S.sidebar, width: sidebarOpen ? 240 : 64, transition: 'width 0.25s ease' }}>
        {/* Logo */}
        <div style={S.logoArea}>
          {sidebarOpen ? (
            <div style={S.logo}>
              <span style={S.logoIcon}>⚡</span>
              <span style={S.logoText}>Admin Panel</span>
            </div>
          ) : (
            <div style={{ ...S.logo, justifyContent: 'center' }}>
              <span style={S.logoIcon}>⚡</span>
            </div>
          )}
          <button style={S.collapseBtn} onClick={() => setSidebarOpen(o => !o)} title="Toggle sidebar">
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Admin info */}
        {sidebarOpen && admin && (
          <div style={S.adminInfo}>
            <div style={S.adminAvatar}>{(admin.username || 'A')[0].toUpperCase()}</div>
            <div>
              <div style={S.adminName}>{admin.username}</div>
              <div style={S.adminRole}>{admin.isSuperuser ? 'Superuser' : 'Staff'}</div>
            </div>
          </div>
        )}

        <div style={S.divider} />

        {/* Nav items */}
        <nav style={S.nav}>
          {NAV.map(item => {
            const active = getActive() === item.path;
            return (
              <button
                key={item.path}
                style={{ ...S.navItem, ...(active ? S.navActive : {}) }}
                onClick={() => navigate(`/admin-panel/${item.path}`)}
                title={!sidebarOpen ? item.label : ''}
              >
                <span style={S.navIcon}>{item.icon}</span>
                {sidebarOpen && <span style={S.navLabel}>{item.label}</span>}
                {active && <span style={S.activeDot} />}
              </button>
            );
          })}
        </nav>

        <div style={{ flex: 1 }} />

        {/* Back to app */}
        <button
          style={{ ...S.navItem, marginBottom: 12, color: '#64748b' }}
          onClick={() => navigate('/chat')}
          title="Back to App"
        >
          <span style={S.navIcon}>🔙</span>
          {sidebarOpen && <span style={S.navLabel}>Back to App</span>}
        </button>
      </aside>

      {/* Main content */}
      <main style={S.main}>
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="users/*" element={<AdminUsers />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="random-chat" element={<AdminRandomChat />} />
          <Route path="posts" element={<AdminPosts />} />
          <Route path="files" element={<AdminFiles />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="feedback" element={<AdminFeedback />} />
        </Routes>
      </main>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.3); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.5); }
      `}</style>
    </div>
  );
}

const S = {
  root: { display: 'flex', height: '100vh', background: '#07070d', fontFamily: "'Inter', 'Segoe UI', sans-serif", overflow: 'hidden' },
  sidebar: {
    background: 'rgba(15,15,25,0.98)',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', flexShrink: 0,
    backdropFilter: 'blur(20px)',
  },
  logoArea: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 16px 14px' },
  logo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: { fontSize: 20, filter: 'drop-shadow(0 0 8px #6366f1)' },
  logoText: { fontWeight: 800, fontSize: 14, color: '#e2e8f0', letterSpacing: '-0.3px' },
  collapseBtn: { background: 'rgba(255,255,255,0.05)', border: 'none', color: '#475569', cursor: 'pointer', borderRadius: 6, padding: '4px 8px', fontSize: 10 },
  adminInfo: { display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 12px' },
  adminAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, color: '#fff', fontSize: 13, flexShrink: 0,
  },
  adminName: { fontWeight: 600, color: '#e2e8f0', fontSize: 11 },
  adminRole: { fontSize: 10, color: '#818cf8', fontWeight: 600, marginTop: 1 },
  divider: { height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 12px 8px' },
  nav: { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '10px 12px', borderRadius: 10, border: 'none',
    background: 'transparent', color: '#64748b', cursor: 'pointer',
    fontSize: 12, fontWeight: 500, textAlign: 'left', transition: 'all 0.15s',
    position: 'relative', whiteSpace: 'nowrap', overflow: 'hidden',
  },
  navActive: { background: 'rgba(99,102,241,0.15)', color: '#818cf8' },
  navIcon: { fontSize: 16, flexShrink: 0 },
  navLabel: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
  activeDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#6366f1', flexShrink: 0,
    boxShadow: '0 0 6px #6366f1',
  },
  main: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
};
